/**
 * Jovii — WHERE A DOWNLOAD ACTUALLY LANDS.
 *
 * The owner: "when they do their downloads it doesn't stay in the browser. It
 * just downloads directly to the files, like Apple Files or Windows Files,
 * wherever. The user picks exactly where. If they're downloading a series it
 * will download to a folder wherever they pick."
 *
 * ── WHAT THE PLATFORM ACTUALLY ALLOWS ───────────────────────────────────────
 *
 * Exactly one API lets a web page write into a folder the user chose:
 * `showDirectoryPicker`, the File System Access API. Checked against caniuse
 * on 2026-09-14, it is Chrome and Edge on the desktop and NOWHERE ELSE —
 * Safari says no on macOS and iOS, Firefox says no, and Chrome on Android says
 * no. Apple have declined to implement it. So "the user picks the folder" is
 * a thing that can be honoured on a laptop and cannot be honoured on an
 * iPhone, and no amount of code changes that.
 *
 * So there are two strategies, and the UI says which one it is rather than
 * pretending they are the same:
 *
 *   'folder'   Chrome/Edge desktop. The reader picks a folder ONCE; the handle
 *              is kept in IndexedDB and re-used, so a season lands in
 *              `<picked>/Silo/Season 1/` with no further prompts. Bytes stream
 *              straight to disk — no staging copy, no browser quota, and a
 *              12 GB film is as fine as a 700 MB one.
 *
 *   'save'     Everyone else, iPhone and iPad included. The file is staged in
 *              the browser's private storage while it downloads (which is what
 *              makes it resumable), then handed to the BROWSER'S OWN download
 *              mechanism when it completes: on iOS that is the Downloads
 *              manager, and the file appears in the Files app under whatever
 *              folder Safari is set to use — the reader changes that in
 *              Settings > Safari > Downloads, which is as close to "pick the
 *              location" as iOS offers. The staged copy is deleted once it has
 *              been handed over. A series cannot become a real folder here, so
 *              the season and episode go into the FILE NAME instead, which is
 *              what every download manager on the platform does.
 *
 * The staging step is not a preference — on 'save' platforms there is no way
 * to stream to the file system at all, so the bytes have to live somewhere
 * while they arrive, and memory is not an option at these sizes.
 */

/*
 * `queryPermission` / `requestPermission` are part of the File System Access
 * API but not of TypeScript's DOM lib, which only ships what is on a standards
 * track every engine has agreed to. Declared here rather than cast at each
 * call site, so there is one place saying what we believe the browser has.
 */
interface PermissionfulHandle {
  queryPermission(descriptor: { mode: 'read' | 'readwrite' }): Promise<PermissionState>;
  requestPermission(descriptor: { mode: 'read' | 'readwrite' }): Promise<PermissionState>;
}
const withPermission = (handle: FileSystemDirectoryHandle): PermissionfulHandle =>
  handle as unknown as PermissionfulHandle;

const DB = 'jt.fs.v1';
const STORE = 'handles';
const KEY = 'downloads-dir';

export type Strategy = 'folder' | 'save';

/** Which of the two this browser can do. */
export function strategy(): Strategy {
  return typeof (globalThis as { showDirectoryPicker?: unknown }).showDirectoryPicker === 'function'
    ? 'folder'
    : 'save';
}

/* A one-store IndexedDB, because a directory handle is a structured-clonable
   object that localStorage cannot hold. Nothing else in the app needs IDB, so
   this stays small and local rather than becoming a "storage layer". */
function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function put(value: FileSystemDirectoryHandle | null): Promise<void> {
  const db = await open();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    if (value === null) tx.objectStore(STORE).delete(KEY);
    else tx.objectStore(STORE).put(value, KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

async function get(): Promise<FileSystemDirectoryHandle | null> {
  try {
    const db = await open();
    const value = await new Promise<FileSystemDirectoryHandle | null>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(KEY);
      req.onsuccess = () => resolve((req.result as FileSystemDirectoryHandle) ?? null);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return value;
  } catch {
    return null;
  }
}

/**
 * The chosen folder, if there is one AND it is still writable.
 *
 * A handle survives a restart but its PERMISSION may not: the spec lets a
 * browser downgrade it to 'prompt', and asking for it back outside a click is
 * refused. So this returns null in that case and the caller asks the reader to
 * pick again, rather than failing silently at the first write.
 */
export async function currentFolder(): Promise<FileSystemDirectoryHandle | null> {
  const handle = await get();
  if (!handle) return null;
  try {
    const query = await withPermission(handle).queryPermission({ mode: 'readwrite' });
    return query === 'granted' ? handle : null;
  } catch {
    return null;
  }
}

/** True when a folder is remembered, whether or not it is still granted. */
export async function hasFolder(): Promise<boolean> {
  return (await get()) !== null;
}

/** Ask for a folder. MUST be called from a click: the picker requires a gesture. */
export async function pickFolder(): Promise<FileSystemDirectoryHandle | null> {
  const picker = (globalThis as {
    showDirectoryPicker?: (o?: { mode?: string; startIn?: string }) => Promise<FileSystemDirectoryHandle>;
  }).showDirectoryPicker;
  if (!picker) return null;
  try {
    const handle = await picker({ mode: 'readwrite', startIn: 'videos' });
    const granted = await withPermission(handle).requestPermission({ mode: 'readwrite' });
    if (granted !== 'granted') return null;
    await put(handle);
    return handle;
  } catch {
    /* The reader cancelled the picker. Not an error. */
    return null;
  }
}

export async function forgetFolder(): Promise<void> {
  await put(null);
}

/** Characters no major file system will take, plus leading/trailing junk. */
export function safeName(part: string): string {
  return part
    .replace(/[/\\?%*:|"<>]/g, '-')
    .replace(/\s+/g, ' ')
    .replace(/^[.\s]+|[.\s]+$/g, '')
    .slice(0, 120) || 'Jovii';
}

/**
 * `<Title>/Season N/` for an episode, `<picked>/` for a film — created on
 * demand. This is the "if they're downloading a series it will download to a
 * folder" part, and it only exists on the 'folder' strategy.
 */
export async function folderFor(
  root: FileSystemDirectoryHandle,
  title: string,
  season?: number
): Promise<FileSystemDirectoryHandle> {
  if (season === undefined) return root;
  const show = await root.getDirectoryHandle(safeName(title), { create: true });
  return show.getDirectoryHandle(`Season ${season}`, { create: true });
}

/**
 * What the file is called. The season and episode are in the NAME as well as
 * the path, because on the 'save' strategy the path does not exist and this is
 * all the reader will have to go on in the Files app.
 *
 * ALWAYS `.mp4`, AND THERE IS NO LONGER AN `.mkv` CASE.
 *
 * The previous commit made this `.mkv` because the bytes really were Matroska
 * and an honest extension beat a wrong one. They are not any more: every
 * download is either remuxed to MP4 on the device before it is written
 * (`remux.ts`) or copied straight through because it was ALREADY an MP4 with
 * codecs the device plays (`canCopyStraight` in manager.svelte.ts). There is no
 * third path, so there is no second extension — and `mimeFor` follows, which is
 * what stops Safari saving a MacBinary archive.
 */
export function fileName(
  title: string,
  season: number | undefined,
  episode: number | undefined,
  episodeTitle?: string
): string {
  const base = safeName(title);
  if (season === undefined || episode === undefined) return `${base}.mp4`;
  const code = `S${String(season).padStart(2, '0')}E${String(episode).padStart(2, '0')}`;
  const tail = episodeTitle ? ` - ${safeName(episodeTitle)}` : '';
  return `${base} - ${code}${tail}.mp4`;
}

/**
 * Hand a finished file to the browser's own download manager.
 *
 * This is the 'save' strategy's whole ending. An <a download> click on an
 * object URL is the only route on iOS, and it is a real download: Safari's
 * manager takes it, shows it in the Downloads list, and puts it in the Files
 * app. The URL is revoked on a delay rather than immediately — revoking it in
 * the same tick cancels the download in WebKit.
 */
/** The container's real MIME type, from the extension we chose. */
export function mimeFor(name: string): string {
  if (name.endsWith('.mp4') || name.endsWith('.m4v')) return 'video/mp4';
  if (name.endsWith('.webm')) return 'video/webm';
  return 'video/x-matroska';
}

export function handOff(file: File, name: string): void {
  /*
   * THE BLOB MUST OUTLIVE THE DOWNLOAD, and that is not a detail.
   *
   * The first version revoked the URL on a timer AND deleted the staged file
   * immediately after the click. Safari reads a blob download LAZILY — the
   * click only registers the transfer, and WebKit pulls the bytes over the
   * seconds or minutes that follow. Deleting the file underneath it gave the
   * owner a full-screen "Safari can't open the page. The operation couldn't be
   * completed. (WebKitBlobResource error 1.)" the instant a 766 MB episode
   * finished — the resource had gone while the download was still reading it.
   *
   * So: the URL is revoked only when the page is going away (at which point
   * WebKit has either finished or lost it anyway), and the staged FILE is not
   * deleted here at all. `sweepDelivered` in the manager clears it on a later
   * visit, once the download cannot still be in flight.
   */
  /*
   * THE TYPE MUST BE ON THE BLOB, or iOS writes a .bin.
   *
   * The staged File comes out of OPFS named `series:tt...:1:2.bin` (the
   * internal key), and a File read from the file system carries `type: ''`.
   * Safari's download manager believes the blob's type over the `download`
   * attribute, so it saved a 766 MB "MacBinary archive" the owner could not
   * play. Re-wrapping the same bytes with the container's real MIME type is
   * what makes the extension stick.
   */
  const typed = file.type ? file : new File([file], name, { type: mimeFor(name) });
  const url = URL.createObjectURL(typed);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  const release = (): void => URL.revokeObjectURL(url);
  window.addEventListener('pagehide', release, { once: true });
}
